import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { frLocale } from 'ngx-bootstrap/locale';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

import { AppFacade } from './app.facade';
import { AppService } from './app.service';
import { AudioRecorderService } from './audio-recorder.service';
import { LoadingStatus } from './core/domain/events/state_provider';
import UniqueEntityID from './core/domain/unique_entity_id';
import { Dietitian } from './data/dietitian/domain/dietitian';
import { DietitianEventProvider } from './data/dietitian/domain/events/dietitian_event_provider';
import {
  DietitianCreated,
  DietitianMsdpPasswordReset,
  DietitianSignedIn,
  DietitianSignedOut,
  DietitianUpdated,
} from './data/dietitian/domain/events/dietitian_events';
import { SubscriptionEventProvider } from './data/dietitian/domain/events/subscription/subscription_event_provider';
import { SubscriptionCancelled } from './data/dietitian/domain/events/subscription/subscription_events';
import { IncomingMessageEventProvider } from './data/notification/incoming-message-event-provider';
import { PatientSignedIn } from './data/patient/domain/events/patient.events';
import { PatientEventProvider } from './data/patient/domain/events/patient_event_provider';
import { Patient } from './data/patient/domain/patient';
import { ToDo, ToDoProps } from './data/todo/domain/todo';
import { TasksService } from './service/tasks.service';
import { EInvoicingBannerType } from './ui/components/e-invoicing-banner/e-invoicing-banner.component';
import { NewsPopupComponent } from './ui/components/news-popup/news-popup.component';
import { SubscriptionBannerType } from './ui/components/subscription-banner/subscription-banner.component';
import { ToDoModalComponent } from './ui/todo/components/todo-modal/todo-modal.component';

defineLocale('fr', frLocale);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('trialBanner', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(-100%)',
        }),
        animate(400),
      ]),
      transition(':leave', [
        animate(
          400,
          style({
            opacity: 0,
            transform: 'translateY(-100%)',
          }),
        ),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private _dietitianStatusSubscription?: Subscription;
  private _subscriptionSub: Subscription;
  private _eInvoicingSub: Subscription;

  public dietitian?: Dietitian;
  public patient?: Patient;
  public remainingDays: number;
  public bannerType?: SubscriptionBannerType | undefined;
  public bannerEInvoicingType?: EInvoicingBannerType | undefined;

  public bsNewsModalRef?: BsModalRef;

  constructor(
    private facade: AppFacade,
    private router: Router,
    private localeService: BsLocaleService,
    private appService: AppService,
    private dietitianEventProvider: DietitianEventProvider,
    private patientEventProvider: PatientEventProvider,
    private subscriptionEventProvider: SubscriptionEventProvider,
    private incomingMessageProvider: IncomingMessageEventProvider,
    private tasksService: TasksService,
    private modalService: BsModalService,
    private audioRecorderService: AudioRecorderService,
  ) {
    this.localeService.use('fr');
    moment.locale(window.navigator.language);

    this.dietitianEventProvider.events$.subscribe((event) => {
      if (event instanceof DietitianSignedOut) {
        this._subscriptionSub?.unsubscribe();
        this.bannerType = undefined;
      } else if (
        event instanceof DietitianSignedIn ||
        event instanceof DietitianCreated
      ) {
        this.refreshDietitian();
      } else if (event instanceof DietitianUpdated) {
        this.refreshDietitian();
      }
    });

    this.subscriptionEventProvider.events$.subscribe((event) => {
      if (event instanceof SubscriptionCancelled) {
        this.refreshDietitian();
      }
    });

    this.incomingMessageProvider.events$.subscribe((event) => {
      appService.logger.debug('🎃 AppComponent.incomingMessageProvider', event);
      if (
        event &&
        event.entity.props &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('dietitianId') &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('msdpPasswordReset')
      ) {
        this.resetMsdpDietitian();
      }

      if (
        event &&
        event.entity.props &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('subscriptionId') &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('subscriptionStatus') &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('productId') &&
        // eslint-disable-next-line no-prototype-builtins
        event.entity.props.hasOwnProperty('subscriptionType')
      ) {
        this.refreshDietitian();
      }
    });

    this.patientEventProvider.events$.subscribe((event) => {
      if (event instanceof PatientSignedIn) {
        this.refreshPatient();
      }
    });

    this.tasksService.addedToDoSubject.subscribe((target) => {
      const toDo = ToDo.create(
        {
          dietitianId: this.dietitian?.id.toString(),
          target,
          status: 'INITIAL',
          description: '',
          dateStart: new Date(),
        } as ToDoProps,
        new UniqueEntityID('_NEW_'),
      );
      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          toDo,
        },
      };
      this.modalService.show(ToDoModalComponent, initialState);
    });

    this.tasksService.editToDoSubject.subscribe((toDo) => {
      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          toDo,
        },
      };
      this.modalService.show(ToDoModalComponent, initialState);
    });
  }

  ngOnInit(): void {
    // check dietitian status
    this._dietitianStatusSubscription = this.facade.dietitian$.subscribe(
      (state) => {
        // finished loading, force refresh
        if (state.loading != LoadingStatus.LOADING) {
          this.router
            .navigateByUrl(location.href.substring(location.origin.length))
            .then();

          this.dietitian = state.entity;
        }
      },
    );
    this.loadDietitian().then();
    this.loadPatient().then();
  }

  refreshPatient() {
    this.facade.getCurrentPatient().then((patient) => {
      this.patient = patient;
    });
  }

  refreshDietitian() {
    this.facade.getCurrentDietitian().then((dietitian) => {
      this.dietitian = dietitian;
      this.displaySubscriptionBanner();
      this.displayEInvoicingBanner().then();
      this.displayNewsPopup(this.dietitian);
      this.listenOnSubscriptionChange().then();
      this.listenOnEInvoicingChange().then();
    });
  }

  resetMsdpDietitian() {
    if (this.dietitian) {
      this.dietitianEventProvider.dispatch(
        new DietitianMsdpPasswordReset(this.dietitian),
      );
    }
  }

  async loadDietitian() {
    const dietitian = await this.facade.loadCurrentDietitian();
    this.displaySubscriptionBanner();
    this.displayEInvoicingBanner().then();
    this.displayNewsPopup(dietitian);
    this.listenOnSubscriptionChange().then();
    this.listenOnEInvoicingChange().then();
  }

  async loadPatient() {
    await this.facade.loadCurrentPatient();
  }

  async listenOnSubscriptionChange() {
    if (!this.dietitian?.subscription?.subscriptionId) {
      return;
    }

    this._subscriptionSub?.unsubscribe();
    this._subscriptionSub = this.facade
      .listenSubscription(
        this.dietitian?.subscription?.subscriptionId?.id?.toString(),
      )
      .subscribe(async (subscription) => {
        if (this.dietitian?.subscription != subscription) {
          await this.facade.loadCurrentDietitian();
          this.displaySubscriptionBanner();
        }
      });
  }

  async listenOnEInvoicingChange() {
    this._eInvoicingSub?.unsubscribe();
    if (this.dietitian) {
      this._eInvoicingSub = this.facade
        .listenEInvoicingParams(this.dietitian.id.toString())
        .subscribe(async () => {
          this.displayEInvoicingBanner().then();
        });
    }
  }

  async displayEInvoicingBanner() {
    const dietitian = await this.facade.getCurrentDietitian();

    // Exception Compte Demo
    if (dietitian?.id?.toString() === '9XWJf462tHawyXVbebjgc9TpHdw2') {
      this.bannerEInvoicingType = undefined;
    } else {
      if (dietitian?.subscription && dietitian?.subscription?.isActive) {
        const billingFeature = await this.facade.findActiveBilling();
        if (
          dietitian.subscriptionFeatures &&
          dietitian.subscriptionFeatures[billingFeature?.id.toString()] &&
          dietitian.subscriptionFeatures[billingFeature?.id.toString()].isActive
        ) {
          const billParams = await this.facade.getBillParams(
            dietitian.id.toString(),
          );

          const connected =
            dietitian.eInvoicingPartnerRefreshStatus == 'connected' ||
            dietitian.eInvoicingPartnerRefreshStatus == 'open_authentication' ||
            (dietitian.eInvoicingPartnerRefreshToken
              ? dietitian.eInvoicingPartnerRefreshToken.length > 0
              : false);

          if (!connected) {
            if (billParams.delayedIdentifier) {
              this.bannerEInvoicingType = EInvoicingBannerType.legacy;
            } else {
              this.bannerEInvoicingType =
                dietitian.eInvoicingPartnerRefreshStatus !== 'in_progress'
                  ? EInvoicingBannerType.warning
                  : undefined;
            }
          } else {
            this.bannerEInvoicingType = undefined;
          }
        }
      }
    }
    this.refreshEInvoicingBanner();
  }

  displaySubscriptionBanner() {
    // user is not connected, skip
    if (!this.dietitian) {
      return;
    }

    if (
      !this.dietitian?.subscription ||
      (!this.dietitian?.subscription?.isActive &&
        !this.dietitian?.subscription?.isUnpaid &&
        !this.dietitian?.subscription?.isPastDue)
    ) {
      this.bannerType = SubscriptionBannerType.expired;
    } else {
      if (this.dietitian?.subscription.isPendingCancellation) {
        this.bannerType = SubscriptionBannerType.pending_cancel;
        this.remainingDays = moment
          .duration(
            moment(this.dietitian?.subscription?.periodEnd)
              .startOf('days')
              .diff(moment().startOf('days')),
          )
          .asDays();
      } else if (this.dietitian?.subscription.isTrialing) {
        this.bannerType = SubscriptionBannerType.trial;
        this.remainingDays = moment
          .duration(
            moment(this.dietitian?.subscription?.periodEnd)
              .startOf('days')
              .diff(moment().startOf('days')),
          )
          .asDays();
      } else if (this.dietitian?.subscription.isUnpaid) {
        this.bannerType = SubscriptionBannerType.unpaid;
      } else if (this.dietitian?.subscription.isPastDue) {
        this.bannerType = SubscriptionBannerType.unpaid;
      }
    }
    this.refreshBanner();
  }

  displayNewsPopup(dietitian: Dietitian | undefined) {
    // user is not connected, skip
    if (!dietitian) {
      return;
    }

    if (dietitian) {
      this.openNewsPopup(dietitian).then();
    }
  }

  get bannerClass() {
    switch (this.bannerType) {
      case SubscriptionBannerType.trial:
        return 'trial';
      case SubscriptionBannerType.expired:
      case SubscriptionBannerType.unpaid:
        return 'expired';
      case SubscriptionBannerType.pending_cancel:
        return 'pending-cancel';
      default:
        return 'default';
    }
  }

  get bannerEInvoicingClass() {
    switch (this.bannerEInvoicingType) {
      case EInvoicingBannerType.warning:
        return 'warning';
      case EInvoicingBannerType.legacy:
        return 'legacy';
      default:
        return 'default';
    }
  }

  ngOnDestroy(): void {
    this._dietitianStatusSubscription?.unsubscribe();
    this._subscriptionSub?.unsubscribe();
    this._eInvoicingSub?.unsubscribe();
  }

  closeBanner() {
    this.bannerType = undefined;
    this.refreshBanner();
  }

  refreshBanner() {
    this.appService.setBannerDisplayed(this.bannerType != undefined);
  }

  closeEInvoicingBanner() {
    this.bannerEInvoicingType = undefined;
    this.refreshEInvoicingBanner();
  }

  refreshEInvoicingBanner() {
    this.appService.setBannerEInvoicingDisplayed(
      this.bannerEInvoicingType != undefined,
    );
  }

  private async openNewsPopup(dietitian: Dietitian) {
    const news = dietitian.lastNews
      ? await this.facade.findAllNewsAfterDate(dietitian.lastNews)
      : await this.facade.findLastTwoNews();
    if (news && news.length > 0) {
      this.bsNewsModalRef = this.modalService.show(NewsPopupComponent, {
        backdrop: false,
        class: 'modal-lg modal-dialog-centered modal-shadow',
        initialState: { news: news },
      });
      if (this.bsNewsModalRef.content instanceof NewsPopupComponent) {
        this.bsNewsModalRef.content.result.subscribe(async (response) => {
          if (!response.checkbox) {
            this.dietitian = await this.facade.updateDietitianLastNews();
          }
          if (response.action) {
            const levelOne = response.action.split('#');
            const levelTwo = levelOne[0].split(':');
            if (levelTwo[0] == 'navigate') {
              this.router.navigateByUrl(levelOne[1]).then();
            }
          }
        });
      }
    }
  }
}
